/* ============================================================
   ÉKO '77 — product search drawer (native Shopify)

   Replaces the reference's client-side filter (bulk-orders inline JS:
   `qE.addEventListener('input', ...)` walking hard-coded .card nodes)
   with the real thing:

     · typing  -> Predictive Search API through the Section Rendering
                  API, so the markup is rendered by Liquid in
                  sections/x-predictive-search.liquid (prices, srcset and
                  money formatting stay server-side — no client-side
                  money formatting to drift out of sync);
     · Enter   -> the native /search route, no interception.

   Open/close timing mirrors the reference: 120ms before focus, 110ms
   debounce on input.

   Progressive enhancement: with JS off, or predictive search disabled
   in theme settings, the drawer is still a plain GET form pointed at
   routes.search_url, so search keeps working.
   ============================================================ */

class XSearchBar extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-x-search-drawer]');
    this.form = this.querySelector('[data-x-search-form]');
    this.input = this.querySelector('[data-x-search-input]');
    this.panel = this.querySelector('[data-x-search-panel]');
    this.statusEl = this.querySelector('[data-x-search-status]');
    this.closeBtn = this.querySelector('[data-x-search-close]');

    if (!this.drawer || !this.input) return;

    this.sectionId = this.dataset.sectionId || 'x-predictive-search';
    this.minChars = parseInt(this.dataset.minChars, 10) || 2;
    this.debounceMs = parseInt(this.dataset.debounce, 10) || 110;
    this.predictive = this.dataset.predictive === 'true';
    this.bridgeThemeHeader = this.dataset.bridgeThemeHeader === 'true';
    this.resourceLimit = parseInt(this.dataset.resourceLimit, 10) || 6;

    this.cache = {};
    this.abortController = null;
    this.debounceTimer = null;
    this.isOpen = false;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);

    this.bindToggles();

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => {
        // the reference clears the query on close, then slides the bar away
        this.reset();
        this.close();
      });
    }

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
  }

  disconnectedCallback() {
    // the toggle listener has to come off too (same capture flag it went on
    // with), otherwise every theme-editor re-render of this section leaves a
    // dead listener on document still pointing at the detached element — the
    // header button would then open nothing.
    document.removeEventListener('click', this.onToggleClick, true);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    if (this.abortController) this.abortController.abort();
    clearTimeout(this.debounceTimer);
  }

  /* ---------------- wiring the openers ---------------- */

  bindToggles() {
    // Delegated on purpose: the ÉKO header section is a separate task, so the
    // toggle button (snippets/x-search-toggle.liquid) may be injected after this
    // element upgrades, re-rendered by the theme editor, or duplicated for the
    // mobile bar. Anything carrying [data-x-search-toggle] opens this drawer.
    //
    // CAPTURE phase, not bubble — this is the fix for "two search UIs open at
    // once". A toggle can sit inside markup that binds its own handler directly
    // to the clicked node: Dawn's details-modal.js does exactly that on the
    // <summary> of its search icon. Target-phase listeners run BEFORE anything
    // bubbles back up to document, so a bubble-phase preventDefault() here
    // arrived too late — Dawn had already opened its modal programmatically and
    // set body.overflow-hidden, which is why the page also stopped scrolling.
    // Capturing at document runs before the event reaches the target at all, so
    // stopPropagation() below keeps it from ever getting there.
    document.addEventListener('click', this.onToggleClick, true);
  }

  onToggleClick(event) {
    const toggle = event.target.closest('[data-x-search-toggle]');
    if (toggle) {
      event.preventDefault();
      // stop the event reaching a foreign target-phase handler (see bindToggles).
      // Deliberately NOT stopImmediatePropagation: sibling listeners on document
      // itself still run, so nothing else on the page is starved of the click.
      event.stopPropagation();
      // belt and braces for the outside-click closer, which also sits on document
      event.xSearchHandled = true;
      this.toggle();
      return;
    }

    // Transitional bridge: while the stock Dawn header is still on the page,
    // route its search icon into this drawer so there is one search in the
    // theme, not two. Turned off from the theme editor once the ÉKO header
    // renders the real toggle (it does now — the setting is off in
    // sections/header-group.json).
    if (!this.bridgeThemeHeader) return;
    const dawnSearch = event.target.closest('.header__icon--search, [href$="/search"]');
    if (!dawnSearch) return;
    // never swallow a click on our own results-page link
    if (dawnSearch.closest('[data-x-search-panel]')) return;
    if (this.contains(dawnSearch)) return;
    event.preventDefault();
    event.stopPropagation();
    event.xSearchHandled = true;
    this.toggle();
  }

  /* ---------------- open / close ---------------- */

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.setTogglesExpanded(true);
    this.input.setAttribute('aria-expanded', String(Boolean(this.panel && this.panel.innerHTML.trim())));
    // the reference waits out the slide before taking focus
    setTimeout(() => this.input.focus(), 120);
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.setTogglesExpanded(false);
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.input.blur();
  }

  setTogglesExpanded(expanded) {
    document.querySelectorAll('[data-x-search-toggle]').forEach((el) => {
      el.setAttribute('aria-expanded', String(expanded));
      if (!expanded && typeof el.blur === 'function') el.blur();
    });
  }

  reset() {
    this.input.value = '';
    this.clearPanel();
  }

  clearPanel() {
    if (this.panel) this.panel.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.drawer.removeAttribute('data-loading');
  }

  /* ---------------- predictive fetch ---------------- */

  onInput() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.search(), this.debounceMs);
  }

  search() {
    if (!this.predictive || !this.panel) return;

    const term = this.input.value.trim();

    if (term.length < this.minChars) {
      this.clearPanel();
      return;
    }

    if (this.cache[term]) {
      this.render(this.cache[term], term);
      return;
    }

    // Deliberately the same shape Dawn ships, plus the documented resources[limit].
    // No resources[type] filter: leaving it off is what makes Shopify return its
    // own query suggestions ("jollof rice") alongside products. The section
    // decides what to draw, so widening the fetch costs nothing and an untested
    // type string can never 400 the request. Scoping to products happens where
    // it is actually asked for — the form's hidden `type` field, which is what
    // the /search results page reads.
    const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    const url =
      `${base}?q=${encodeURIComponent(term)}` +
      `&section_id=${encodeURIComponent(this.sectionId)}` +
      `&resources[limit]=${this.resourceLimit}`;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.drawer.setAttribute('data-loading', 'true');

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector(`#shopify-section-${this.sectionId}`);
        const markup = section ? section.innerHTML : '';
        this.cache[term] = markup;
        this.render(markup, term);
      })
      .catch((error) => {
        this.drawer.removeAttribute('data-loading');
        // 20 = aborted, which is the expected path for fast typing
        if (error && (error.code === 20 || error.name === 'AbortError')) return;
        this.clearPanel();
      });
  }

  render(markup, term) {
    this.drawer.removeAttribute('data-loading');
    this.panel.innerHTML = markup;

    const hasResults = this.options().length > 0;
    this.input.setAttribute('aria-expanded', String(Boolean(markup.trim())));
    this.input.removeAttribute('aria-activedescendant');

    if (this.statusEl) {
      const count = this.options().length;
      const template = this.statusEl.dataset.template || '';
      this.statusEl.textContent = hasResults
        ? template.replace('[count]', count).replace('[terms]', term)
        : this.statusEl.dataset.empty || '';
    }
  }

  /* ---------------- keyboard ---------------- */

  options() {
    if (!this.panel) return [];
    return Array.from(this.panel.querySelectorAll('[role="option"]'));
  }

  selectedIndex() {
    return this.options().findIndex((el) => el.getAttribute('aria-selected') === 'true');
  }

  moveSelection(step) {
    const options = this.options();
    if (!options.length) return;

    const current = this.selectedIndex();
    let next = current + step;
    if (next < 0) next = options.length - 1;
    if (next > options.length - 1) next = 0;

    options.forEach((el, i) => el.setAttribute('aria-selected', String(i === next)));
    const active = options[next];
    if (active.id) this.input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  onInputKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveSelection(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveSelection(-1);
        break;
      case 'Escape':
        // handled document-wide, but stop it reaching a parent dialog first
        event.stopPropagation();
        this.reset();
        this.close();
        break;
      default:
        break;
    }
  }

  onSubmit(event) {
    // a highlighted suggestion wins over the results page
    const options = this.options();
    const index = this.selectedIndex();
    if (index > -1 && options[index]) {
      const link = options[index].querySelector('a[href]');
      if (link) {
        event.preventDefault();
        window.location.href = link.href;
        return;
      }
    }
    // otherwise: no interception at all — the browser GETs routes.search_url
    if (!this.input.value.trim()) event.preventDefault();
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    this.reset();
    this.close();
  }

  onDocumentClick(event) {
    if (!this.isOpen) return;
    // an opener already dealt with this click
    if (event.xSearchHandled) return;
    if (this.contains(event.target)) return;
    if (event.target.closest('[data-x-search-toggle]')) return;
    this.close();
  }
}

if (!customElements.get('x-search-bar')) {
  customElements.define('x-search-bar', XSearchBar);
}
